import type {
    ValueLedgerApiLedger,
    ValueLedgerApiOutcome,
    ValueLedgerFilterItem,
    ValueLedgerRangeOption,
    ValueLedgerWorkLocation,
} from '@common/api/valueLedgerService';

export interface ValueLedgerDesignData {
  schemaVersion: string;
  status: string;
  reasonCode: string | null;
  ctaAction: string | null;
  currentGeneration: ValueLedgerCurrentGeneration | null;
  generatedAt: string;
  sourceEnvironment: string;
  workspace: {
    workspaceId: string;
    workspaceName: string;
    workspaceAvatar: string | null;
    initials: string;
    viewerRole: string;
    ledgerTitle: string;
    lastGeneratedAt: string;
    ledgerRefreshSchedule: {
      frequency: 'daily' | string;
      localTime: string;
      internalTriggerLocalTime: string | null;
      timezone: string;
    } | null;
    timezone: string;
  };
  range: {
    availableRanges: ValueLedgerRangeDisplayOption[];
    selectedRangeId: string;
    rangeLabel: string;
    rangeStartAt: string;
    rangeEndAt: string;
    status: string | null;
    generatedAt: string | null;
    sourceRangeStartAt: string | null;
    sourceRangeEndAt: string | null;
    emptyReason: string | null;
    isCurrentCycle: boolean;
    subscriptionCycleNo: number | null;
  };
  summary: {
    memberTaskCount: number;
    bustlyOutcomeCount: number;
    visibleOutcomeCount: number;
    estimatedTimeSavedMinutes: number | null;
    estimatedLaborCostAmount: number | null;
    estimatedEconomicValueAmount: number | null;
    currency: string;
    valueCalculationStatus: string;
    valueInputsMissing: string[];
    totalDisplayCostUsd: number;
    totalTokens: number;
  };
  overview: ValueLedgerOverview;
  filters: {
    outcomeTypeCounts: Array<{ label: string; count: number; }>;
    businessModuleCounts: Array<{ label: string; count: number; }>;
    taskTypeCounts: Array<{ label: string; count: number; }>;
    acceptanceStatusCounts: Array<{ label: string; count: number; }>;
  };
  outcomes: ValueLedgerOutcome[];
  tasks: ValueLedgerTask[];
}

export interface ValueLedgerOverview {
  headline: string | null;
  description: string | null;
  keyMetrics: {
    userStartedTaskCount: number | null;
    activeMemberCount: number | null;
    coreScenarioTaskCount: number | null;
    coreScenarioTaskShare: number | null;
    coreScenarioLabel: string | null;
  };
  valueJudgment: {
    title: string | null;
    description: string | null;
  };
  taskScenarios: ValueLedgerOverviewTaskScenario[];
  insights: ValueLedgerOverviewInsights;
}

export interface ValueLedgerOverviewTaskScenario {
  scenarioLabel: string | null;
  taskCount: number | null;
  activeMemberCount: number | null;
  representativeWork: string | null;
  sourceTaskIds: string[];
}

export interface ValueLedgerOverviewInsightCard {
  title: string | null;
  description: string | null;
}

export interface ValueLedgerOverviewInsightGroup {
  title: string | null;
  description: string | null;
  checklist: string[];
}

export interface ValueLedgerOverviewChampion {
  name: string | null;
  taskCount: number | null;
  focus: string | null;
}

export interface ValueLedgerOverviewInsights {
  efficiencyHighlights: ValueLedgerOverviewInsightCard[];
  standardWorkflow: ValueLedgerOverviewInsightGroup;
  reviewSuggestion: ValueLedgerOverviewInsightGroup;
  champions: ValueLedgerOverviewChampion[];
  nextCycleFocus: ValueLedgerOverviewInsightCard[];
}

export interface ValueLedgerRangeDisplayOption {
  rangeId: string;
  label: string;
  startAt: string;
  endAt: string;
  timezone?: string | null;
  snapshotId?: string | null;
  status?: string | null;
  generatedAt?: string | null;
  createdAt?: string | null;
  runStartedAt?: string | null;
  sourceRangeStartAt?: string | null;
  sourceRangeEndAt?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  emptyReason?: string | null;
  isCurrentCycle?: boolean | null;
}

export interface ValueLedgerCurrentGeneration {
  snapshotId?: string | null;
  status?: string | null;
  rangeId?: string | null;
  requestedAt?: string | null;
  runStartedAt?: string | null;
  generatedAt?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  [key: string]: unknown;
}

export interface ValueLedgerDerivedField<T> {
  value: T;
  localizedValue?: {
    en?: T;
    zh?: T;
  };
  source: string;
  confidence?: string;
  reasonCodes?: string[];
}

export interface ValueLedgerEvidenceSystem {
  code: string;
  label: string;
  source: string;
  confidence?: string;
  operationCounts?: {
    read?: number;
    write?: number;
    check?: number;
    other?: number;
    total?: number;
  };
}

export interface ValueLedgerOutcomeDetailProductData {
  outcomeDescription?: ValueLedgerDerivedField<string>;
  detailOutcomeType?: { code: string; label: string; source: string; };
  detailBusinessArea?: { code: string; label: string; source: string; };
  detailSourceTask?: {
    id: string;
    title: string;
    lastActivityAt: string | null;
    source: string;
  };
  concreteOutput?: ValueLedgerDerivedField<string>;
  classificationBasis?: {
    reasonText: string;
    localizedReasonText?: {
      en?: string;
      zh?: string;
    };
    typeDefinition: string;
    localizedTypeDefinition?: {
      en?: string;
      zh?: string;
    };
    matchedRules: string[];
    source: string;
    confidence?: string;
  };
  scope?: {
    label: string;
    value: string;
    objectType: string | null;
    objectId: string | null;
    externalUrl: string | null;
    source: string;
    confidence?: string;
  };
  systemEvidence?: {
    systems: ValueLedgerEvidenceSystem[];
    memberMessageCount: number;
    assistantMessageCount: number;
    toolCallCount: number;
    source: string;
  };
  completionResultText?: ValueLedgerDerivedField<string>;
}

export interface ValueLedgerOutcome {
  outcomeId: string;
  traceId: string;
  outcomeNo: string;
  sequence: number;
  title: string;
  summary: string;
  outcomeType: string;
  outcomeTypeLabel: string;
  businessModule: string;
  taskType: string;
  acceptanceStatus: string;
  scopeLabel: string;
  scopeValue: string;
  evidenceSystems: string[];
  executionActionCount: number;
  memberMessageCount: number;
  collaborationRecordCount: number;
  sourceTaskId: string;
  sourceTaskNo: string;
  sourceTaskTitle: string;
  sourceTaskCompletedAt: string | null;
  sourceTaskTraceId: string;
  sourceUserName: string | null;
  sourceUserEmail: string | null;
  updatedAt: string | null;
  rangeLabel: string;
  memberNextStep: string | null;
  bustlyProcessingJudgment: string | null;
  classificationBasis: string | null;
  billingAcceptanceBasis: string | null;
  acceptanceSuggestion: string | null;
  riskNextStep: string | null;
  nonBillableCheck: string | null;
  disputeBasis: string | null;
  displayCostUsd: number;
  totalTokens: number | null;
  assistantMessageCount: number;
  billingEventCount: number;
  cardTitle?: ValueLedgerDerivedField<string>;
  detailTitle?: ValueLedgerDerivedField<string>;
  cardDescription?: ValueLedgerDerivedField<string>;
  whereBustlyWorked?: ValueLedgerEvidenceSystem[];
  detailLoaded: boolean;
  detail?: ValueLedgerOutcomeDetailProductData;
  traceEvidence?: {
    memberMessageCount: number;
    assistantMessageCount: number;
    toolCallCount: number;
    billingEventCount: number;
    displayCostUsd: number;
    totalTokens: number | null;
    dataStatus: string;
  };
  conversationExcerpts?: Array<{
    role: string;
    source: string;
    messageAt: string;
    excerpt: string;
  }>;
}

export interface ValueLedgerTaskOutcome {
  outcomeId: string;
  outcomeNo: string;
  title: string;
  summary: string;
  outcomeType: string;
  businessModule: string;
  acceptanceStatus: string;
}

export interface ValueLedgerTask {
  taskId: string;
  taskNo: string;
  traceId: string;
  title: string;
  taskType: string;
  taskStatus: string;
  startedAt: string;
  completedAt: string;
  collaborationRecordCount: number;
  memberMessageCount: number;
  systemExecutionCount: number;
  outcomeCount: number;
  conversationCount: number;
  outcomes: ValueLedgerTaskOutcome[];
}

export interface ValueLedgerAdapterOptions {
    workspaceName?: string | null;
    viewerRole?: string | null;
    environment?: string | null;
    locale?: string | null;
}

const DEFAULT_TIMEZONE = 'Asia/Shanghai';
const DEFAULT_RANGE_ID = 'current';
const DEFAULT_RANGE_LABEL = '当前订阅周期';
const sourceTaskTimestampPrefixPattern = /^\s*\[(?:(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+)?\d{4}[-/]\d{1,2}[-/]\d{1,2}\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s+(?:GMT|UTC)[+-]?\d{1,2}(?::?\d{2})?)?\]\s*(.+)$/i;

const getLocalizedAdapterFallbacks = (locale?: string | null) => {
    const isChinese = String(locale || '').toLowerCase().startsWith('zh');
    return {
        unknownSystem: isChinese ? '未知' : 'Unknown',
        ownerView: isChinese ? '所有者视图' : 'Owner view',
    };
};

const stringValue = (value: unknown, fallback = ''): string => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text || fallback;
};

const sourceTaskTitleValue = (value: unknown, fallback = ''): string => {
    const text = stringValue(value, fallback);
    const timestampPrefixMatch = text.match(sourceTaskTimestampPrefixPattern);
    return timestampPrefixMatch ? stringValue(timestampPrefixMatch[1], text) : text;
};

const optionalString = (value: unknown): string | null => {
    const text = stringValue(value, '');
    return text || null;
};

const numberValue = (value: unknown, fallback = 0): number => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
};

const optionalNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const deriveInitials = (name: string): string => {
    const words = name.split(/\s+/u).filter(Boolean);
    if (words.length === 0) return 'WS';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const derivedText = (
    value: unknown,
    fallback: string,
    source = 'outcome_ledger_api',
): ValueLedgerDerivedField<string> => {
    if (value && typeof value === 'object' && 'value' in value) {
        const field = value as {
            value?: unknown;
            localizedValue?: { en?: string; zh?: string };
            source?: string;
            confidence?: string;
            reasonCodes?: string[];
        };

        return {
            value: stringValue(field.value, fallback),
            localizedValue: field.localizedValue,
            source: field.source || source,
            confidence: field.confidence,
            reasonCodes: field.reasonCodes,
        };
    }

    return {
        value: stringValue(value, fallback),
        source,
    };
};

const filterCounts = (items: ValueLedgerFilterItem[] | undefined): Array<{ label: string; count: number; }> =>
    (items ?? [])
        .map((item) => ({
            label: stringValue(item.label ?? item.code, ''),
            count: numberValue(item.count),
        }))
        .filter((item) => item.label);

const toRangeOption = (range: ValueLedgerRangeOption): ValueLedgerDesignData['range']['availableRanges'][number] => ({
    rangeId: stringValue(range.rangeId, DEFAULT_RANGE_ID),
    label: stringValue(range.label, DEFAULT_RANGE_LABEL),
    startAt: stringValue(range.startAt, ''),
    endAt: stringValue(range.endAt, ''),
    timezone: range.timezone,
    snapshotId: range.snapshotId,
    status: optionalString(range.status),
    generatedAt: optionalString(range.generatedAt),
    createdAt: optionalString(range.createdAt),
    runStartedAt: optionalString(range.runStartedAt),
    sourceRangeStartAt: optionalString(range.sourceRangeStartAt),
    sourceRangeEndAt: optionalString(range.sourceRangeEndAt),
    errorCode: optionalString(range.errorCode),
    errorMessage: optionalString(range.errorMessage),
    emptyReason: optionalString(range.emptyReason),
    isCurrentCycle: typeof range.isCurrentCycle === 'boolean' ? range.isCurrentCycle : null,
});

const dedupeRangeOptions = (
    ranges: ValueLedgerDesignData['range']['availableRanges'],
    selectedRangeId?: string | null,
): ValueLedgerDesignData['range']['availableRanges'] => {
    const byRangeKey = new Map<string, ValueLedgerDesignData['range']['availableRanges'][number]>();

    ranges.forEach((range) => {
        const rangeKey = range.startAt && range.endAt
            ? `${range.startAt}|${range.endAt}|${range.timezone ?? ''}`
            : range.rangeId;
        const existing = byRangeKey.get(rangeKey);
        if (!existing) {
            byRangeKey.set(rangeKey, range);
            return;
        }

        const shouldPreferRange = range.rangeId === selectedRangeId && existing.rangeId !== selectedRangeId;
        const preferred = shouldPreferRange ? range : existing;
        const fallback = shouldPreferRange ? existing : range;

        byRangeKey.set(rangeKey, {
            ...preferred,
            startAt: preferred.startAt || fallback.startAt,
            endAt: preferred.endAt || fallback.endAt,
            timezone: preferred.timezone ?? fallback.timezone,
            snapshotId: preferred.snapshotId ?? fallback.snapshotId,
            status: preferred.status ?? fallback.status,
            generatedAt: preferred.generatedAt ?? fallback.generatedAt,
            createdAt: preferred.createdAt ?? fallback.createdAt,
            runStartedAt: preferred.runStartedAt ?? fallback.runStartedAt,
            sourceRangeStartAt: preferred.sourceRangeStartAt ?? fallback.sourceRangeStartAt,
            sourceRangeEndAt: preferred.sourceRangeEndAt ?? fallback.sourceRangeEndAt,
            errorCode: preferred.errorCode ?? fallback.errorCode,
            errorMessage: preferred.errorMessage ?? fallback.errorMessage,
            emptyReason: preferred.emptyReason ?? fallback.emptyReason,
            isCurrentCycle: preferred.isCurrentCycle ?? fallback.isCurrentCycle,
        });
    });

    return Array.from(byRangeKey.values());
};

const isBillingWindowRange = (rangeId: string | null | undefined) => (
    typeof rangeId === 'string' && rangeId.trim().startsWith('billing-window:')
);

const isTrialWindowRange = (rangeId: string | null | undefined) => (
    typeof rangeId === 'string' && rangeId.trim().startsWith('trial-window:')
);

const isSubscriptionPeriodRange = (rangeId: string | null | undefined) => (
    isBillingWindowRange(rangeId) || isTrialWindowRange(rangeId)
);

const preferSubscriptionPeriodRangeOptions = (
    ranges: ValueLedgerDesignData['range']['availableRanges'],
): ValueLedgerDesignData['range']['availableRanges'] => {
    const subscriptionPeriodRanges = ranges.filter((range) => isSubscriptionPeriodRange(range.rangeId));
    return subscriptionPeriodRanges.length > 0 ? subscriptionPeriodRanges : ranges;
};

const readScope = (detail: Record<string, any> | null | undefined) => {
    const scope = detail?.scope ?? {};
    return {
        label: stringValue(scope.label, '处理范围'),
        value: stringValue(scope.value, '-'),
        objectType: scope.objectType ?? null,
        objectId: scope.objectId ?? null,
        externalUrl: scope.externalUrl ?? null,
        source: stringValue(scope.source, 'outcome_ledger_api'),
        confidence: scope.confidence,
    };
};

const toOperationCounts = (counts: ValueLedgerWorkLocation['operationCounts']) => {
    if (!counts) return undefined;
    const result = {
        read: optionalNumber(counts.read) ?? undefined,
        write: optionalNumber(counts.write) ?? undefined,
        check: optionalNumber(counts.check) ?? undefined,
        other: optionalNumber(counts.other) ?? undefined,
        total: optionalNumber(counts.total) ?? undefined,
    };

    return Object.values(result).some((value) => value !== undefined) ? result : undefined;
};

const toEvidenceSystem = (
    location: ValueLedgerWorkLocation,
    fallbackLabel: string,
): ValueLedgerEvidenceSystem => ({
    code: stringValue(location.code, 'unknown'),
    label: stringValue(location.label ?? location.code, fallbackLabel),
    source: stringValue(location.source, 'outcome_ledger_api'),
    confidence: location.confidence || undefined,
    operationCounts: toOperationCounts(location.operationCounts),
});

const readOutcomeSourceUser = (
    outcome: ValueLedgerApiOutcome,
    detail: Record<string, any>,
) => {
    const detailSourceTask = detail.detailSourceTask ?? detail.sourceTask ?? {};
    const sourceUserEmail = optionalString(
        outcome.sourceTaskUserEmail
        ?? outcome.source_task_user_email
        ?? outcome.sessionUserEmail
        ?? outcome.session_user_email
        ?? outcome.memberEmail
        ?? outcome.member_email
        ?? outcome.userEmail
        ?? outcome.user_email
        ?? outcome.requesterEmail
        ?? outcome.requester_email
        ?? detailSourceTask.userEmail
        ?? detailSourceTask.user_email
        ?? detailSourceTask.sessionUserEmail
        ?? detailSourceTask.session_user_email,
    );
    const sourceUserName = optionalString(
        outcome.sourceTaskUserName
        ?? outcome.source_task_user_name
        ?? outcome.sessionUserName
        ?? outcome.session_user_name
        ?? outcome.memberName
        ?? outcome.member_name
        ?? outcome.userName
        ?? outcome.user_name
        ?? outcome.requesterName
        ?? outcome.requester_name
        ?? detailSourceTask.userName
        ?? detailSourceTask.user_name
        ?? detailSourceTask.sessionUserName
        ?? detailSourceTask.session_user_name,
    );

    return {
        sourceUserName,
        sourceUserEmail,
    };
};

const toCurrentGeneration = (
    value: ValueLedgerApiLedger['currentGeneration'],
): ValueLedgerCurrentGeneration | null => {
    if (!value || typeof value !== 'object') return null;
    return { ...value } as ValueLedgerCurrentGeneration;
};

const toOverviewTaskScenario = (item: Record<string, unknown>): ValueLedgerOverviewTaskScenario => ({
    scenarioLabel: optionalString(
        item.scenarioLabel
        ?? item.taskAreaLabel
        ?? item.businessAreaLabel
        ?? item.businessModuleLabel
        ?? item.label
        ?? item.name,
    ),
    taskCount: optionalNumber(item.taskCount),
    activeMemberCount: optionalNumber(
        item.activeMemberCount
        ?? item.participantCount
        ?? item.participant_count
        ?? item.memberCount
        ?? item.userCount,
    ),
    representativeWork: optionalString(sourceTaskTitleValue(item.representativeWork ?? item.sampleWork ?? item.exampleWork)),
    sourceTaskIds: Array.isArray(item.sourceTaskIds)
        ? item.sourceTaskIds.map((value) => stringValue(value, '')).filter(Boolean)
        : [],
});

const toInsightChecklist = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];

    return value
        .map((item) => {
            if (typeof item === 'string') return optionalString(item);
            if (!item || typeof item !== 'object') return null;
            const record = item as Record<string, unknown>;
            return optionalString(record.text ?? record.label ?? record.title ?? record.value);
        })
        .filter((item): item is string => Boolean(item));
};

const toInsightCard = (item: Record<string, unknown>): ValueLedgerOverviewInsightCard => ({
    title: optionalString(item.title ?? item.label ?? item.name),
    description: optionalString(item.description ?? item.summary ?? item.value),
});

const toInsightCards = (value: unknown): ValueLedgerOverviewInsightCard[] => {
    if (!Array.isArray(value)) return [];
    return value
        .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
        .map(toInsightCard);
};

const toInsightGroup = (value: unknown): ValueLedgerOverviewInsightGroup => {
    const record = value && typeof value === 'object'
        ? value as Record<string, unknown>
        : {};

    return {
        title: optionalString(record.title ?? record.label ?? record.name),
        description: optionalString(record.description ?? record.summary),
        checklist: toInsightChecklist(record.checklist ?? record.items ?? record.steps),
    };
};

const toInsightChampions = (value: unknown): ValueLedgerOverviewChampion[] => {
    if (!Array.isArray(value)) return [];

    return value
        .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
        .map((item) => ({
            name: optionalString(item.name ?? item.userName ?? item.email ?? item.label),
            taskCount: optionalNumber(item.taskCount ?? item.tasksCount ?? item.count),
            focus: optionalString(item.focus ?? item.description ?? item.summary),
        }));
};

const toOverviewInsights = (value: unknown): ValueLedgerOverviewInsights => {
    const insights = value && typeof value === 'object'
        ? value as Record<string, unknown>
        : {};

    return {
        efficiencyHighlights: toInsightCards(
            insights.efficiencyHighlights
            ?? insights.efficiencySignals
            ?? insights.improvements
            ?? insights.valueSignals,
        ),
        standardWorkflow: toInsightGroup(
            insights.standardWorkflow
            ?? insights.workflowToStandardize
            ?? insights.firstWorkflowToStandardize,
        ),
        reviewSuggestion: toInsightGroup(insights.reviewSuggestion ?? insights.operatingRhythm),
        champions: toInsightChampions(insights.champions ?? insights.championUsers),
        nextCycleFocus: toInsightCards(
            insights.nextCycleFocus
            ?? insights.nextCycleGoals
            ?? insights.recommendedGoals,
        ),
    };
};

const toOverview = (
    api: ValueLedgerApiLedger,
    memberTaskCount: number,
): ValueLedgerOverview => {
    const overview = api.overview ?? api.outcomeOverview ?? null;
    const keyMetrics = overview?.keyMetrics ?? {};
    const taskScenariosSource = overview?.taskScenarioBreakdown ?? overview?.taskScenarios ?? [];
    const insightsSource = overview?.insights ?? overview?.aiInsights ?? null;
    const userStartedTaskCount = optionalNumber(
        keyMetrics.userStartedTaskCount
        ?? api.summary?.memberTaskCount
        ?? api.summary?.userTaskCount,
    )
        ?? memberTaskCount;

    return {
        headline: optionalString(overview?.headline),
        description: optionalString(overview?.description),
        keyMetrics: {
            userStartedTaskCount,
            activeMemberCount: optionalNumber(keyMetrics.activeMemberCount),
            coreScenarioTaskCount: optionalNumber(keyMetrics.coreScenarioTaskCount),
            coreScenarioTaskShare: optionalNumber(keyMetrics.coreScenarioTaskShare),
            coreScenarioLabel: optionalString(keyMetrics.coreScenarioLabel),
        },
        valueJudgment: {
            title: optionalString(overview?.valueJudgment?.title),
            description: optionalString(overview?.valueJudgment?.description),
        },
        taskScenarios: taskScenariosSource
            .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
            .map(toOverviewTaskScenario),
        insights: toOverviewInsights(insightsSource),
    };
};

const toOutcome = (
    outcome: ValueLedgerApiOutcome,
    rangeLabel: string,
    generatedAt: string,
    fallbackLabels: ReturnType<typeof getLocalizedAdapterFallbacks>,
): ValueLedgerOutcome => {
    const detail = outcome.detail ?? {};
    const scope = readScope(detail);
    const evidenceSummary = detail.evidenceSummary ?? {};
    const locations = outcome.workLocations ?? outcome.whereBustlyWorked ?? [];
    const systems = locations.map((location) => toEvidenceSystem(location, fallbackLabels.unknownSystem));
    const outcomeTypeLabel = stringValue(outcome.outcomeTypeLabel ?? outcome.outcomeType, '未分类成果');
    const businessModule = stringValue(
        outcome.businessAreaLabel ?? outcome.businessArea ?? outcome.businessModuleLabel ?? outcome.businessModule,
        '未分类业务板块',
    );
    const sourceTaskRef = stringValue(outcome.sourceTaskRef, stringValue(outcome.outcomeId, '-'));
    const sourceTaskTitle = sourceTaskTitleValue(outcome.sourceTaskTitle, stringValue(outcome.title, '-'));
    const sourceTaskTime = optionalString(outcome.sourceTaskTime);
    const sourceUser = readOutcomeSourceUser(outcome, detail);
    const memberMessageCount = numberValue(evidenceSummary.userMessageCount);
    const assistantMessageCount = numberValue(evidenceSummary.bustlyReplyCount);
    const executionActionCount = numberValue(evidenceSummary.actionCount);
    const title = stringValue(outcome.title ?? outcome.cardTitle, '-');
    const summary = stringValue(outcome.summary ?? outcome.cardDescription, '-');

    return {
        outcomeId: stringValue(outcome.outcomeId),
        traceId: sourceTaskRef,
        outcomeNo: stringValue(outcome.outcomeNo, '0'),
        sequence: numberValue(outcome.sequence, numberValue(outcome.outcomeNo, 0)),
        title,
        summary,
        outcomeType: stringValue(outcome.outcomeType, outcomeTypeLabel),
        outcomeTypeLabel,
        businessModule,
        taskType: outcomeTypeLabel,
        acceptanceStatus: stringValue(outcome.acceptanceStatusLabel ?? outcome.acceptanceStatus, '待复核'),
        scopeLabel: scope.label,
        scopeValue: scope.value,
        evidenceSystems: systems.map((system) => system.label),
        executionActionCount,
        memberMessageCount,
        collaborationRecordCount: memberMessageCount + assistantMessageCount + executionActionCount,
        sourceTaskId: sourceTaskRef,
        sourceTaskNo: sourceTaskRef,
        sourceTaskTitle,
        sourceTaskCompletedAt: sourceTaskTime,
        sourceTaskTraceId: sourceTaskRef,
        sourceUserName: sourceUser.sourceUserName,
        sourceUserEmail: sourceUser.sourceUserEmail,
        updatedAt: sourceTaskTime ?? generatedAt,
        rangeLabel,
        memberNextStep: null,
        bustlyProcessingJudgment: null,
        classificationBasis: optionalString(detail.classificationReason),
        billingAcceptanceBasis: null,
        acceptanceSuggestion: optionalString(detail.acceptanceSuggestion),
        riskNextStep: optionalString(detail.riskAndNextStep),
        nonBillableCheck: null,
        disputeBasis: null,
        displayCostUsd: 0,
        totalTokens: null,
        assistantMessageCount,
        billingEventCount: 0,
        cardTitle: derivedText(outcome.cardTitle, title),
        detailTitle: derivedText(detail.detailTitle, title),
        cardDescription: derivedText(outcome.cardDescription, summary),
        whereBustlyWorked: systems,
        detailLoaded: Boolean(outcome.detail),
        detail: {
            outcomeDescription: derivedText(detail.outcomeDescription, summary),
            detailOutcomeType: {
                code: stringValue(outcome.outcomeType, outcomeTypeLabel),
                label: outcomeTypeLabel,
                source: 'outcome_ledger_api',
            },
            detailBusinessArea: {
                code: stringValue(outcome.businessArea ?? outcome.businessModule, businessModule),
                label: businessModule,
                source: 'outcome_ledger_api',
            },
            detailSourceTask: {
                id: sourceTaskRef,
                title: sourceTaskTitle,
                lastActivityAt: sourceTaskTime,
                source: 'outcome_ledger_api',
            },
            concreteOutput: derivedText(detail.concreteOutput, summary),
            classificationBasis: {
                reasonText: stringValue(detail.classificationReason, '-'),
                typeDefinition: stringValue(detail.typeDefinition, '-'),
                matchedRules: [],
                source: 'outcome_ledger_api',
                confidence: optionalString(scope.confidence) || undefined,
            },
            scope,
            systemEvidence: {
                systems,
                memberMessageCount,
                assistantMessageCount,
                toolCallCount: executionActionCount,
                source: 'outcome_ledger_api',
            },
            completionResultText: derivedText(detail.completionResultText, summary),
        },
        traceEvidence: {
            memberMessageCount,
            assistantMessageCount,
            toolCallCount: executionActionCount,
            billingEventCount: 0,
            displayCostUsd: 0,
            totalTokens: null,
            dataStatus: 'trace_derived',
        },
        conversationExcerpts: [],
    };
};

const toTasks = (outcomes: ValueLedgerOutcome[]): ValueLedgerTask[] => {
    const tasks = new Map<string, ValueLedgerTask>();

    outcomes.forEach((outcome) => {
        const existing = tasks.get(outcome.sourceTaskId);
        const taskOutcome: ValueLedgerTaskOutcome = {
            outcomeId: outcome.outcomeId,
            outcomeNo: outcome.outcomeNo,
            title: outcome.title,
            summary: outcome.summary,
            outcomeType: outcome.outcomeTypeLabel,
            businessModule: outcome.businessModule,
            acceptanceStatus: outcome.acceptanceStatus,
        };

        if (existing) {
            existing.outcomes.push(taskOutcome);
            existing.outcomeCount = existing.outcomes.length;
            return;
        }

        tasks.set(outcome.sourceTaskId, {
            taskId: outcome.sourceTaskId,
            taskNo: outcome.sourceTaskNo,
            traceId: outcome.sourceTaskTraceId,
            title: outcome.sourceTaskTitle,
            taskType: outcome.taskType,
            taskStatus: 'completed',
            startedAt: outcome.sourceTaskCompletedAt ?? '',
            completedAt: outcome.sourceTaskCompletedAt ?? '',
            collaborationRecordCount: outcome.collaborationRecordCount,
            memberMessageCount: outcome.memberMessageCount,
            systemExecutionCount: outcome.executionActionCount,
            outcomeCount: 1,
            conversationCount: outcome.memberMessageCount + outcome.assistantMessageCount,
            outcomes: [taskOutcome],
        });
    });

    return Array.from(tasks.values());
};

export const adaptValueLedgerApiOutcome = (
    outcome: ValueLedgerApiOutcome,
    options: ValueLedgerAdapterOptions & { rangeLabel?: string | null; generatedAt?: string | null } = {},
): ValueLedgerOutcome => {
    const fallbackLabels = getLocalizedAdapterFallbacks(options.locale);
    return toOutcome(
        outcome,
        stringValue(options.rangeLabel, DEFAULT_RANGE_LABEL),
        stringValue(options.generatedAt, ''),
        fallbackLabels,
    );
};

export const adaptValueLedgerApiResponse = (
    api: ValueLedgerApiLedger,
    options: ValueLedgerAdapterOptions = {},
): ValueLedgerDesignData => {
    const generatedAt = stringValue(api.ledgerGeneratedAt ?? api.generatedAt, '');
    const workspaceName = stringValue(
        api.workspace?.workspaceName ?? options.workspaceName,
        '当前工作区',
    );
    const timezone = stringValue(
        api.range?.timezone ?? api.workspace?.timezone ?? api.ledgerRefreshSchedule?.timezone,
        DEFAULT_TIMEZONE,
    );
    const fallbackRange = {
        rangeId: stringValue(api.range?.selectedRangeId, DEFAULT_RANGE_ID),
        label: stringValue(api.range?.rangeLabel, DEFAULT_RANGE_LABEL),
        startAt: stringValue(api.range?.rangeStartAt, ''),
        endAt: stringValue(api.range?.rangeEndAt, ''),
        timezone,
        status: optionalString(api.range?.status),
        generatedAt: optionalString(api.range?.generatedAt),
        sourceRangeStartAt: optionalString(api.range?.sourceRangeStartAt),
        sourceRangeEndAt: optionalString(api.range?.sourceRangeEndAt),
        emptyReason: optionalString(api.range?.emptyReason),
    };
    const availableRanges = preferSubscriptionPeriodRangeOptions(dedupeRangeOptions(
        (api.range?.availableRanges ?? []).map(toRangeOption),
        api.range?.selectedRangeId,
    ));
    const ranges = availableRanges.length > 0 ? availableRanges : [fallbackRange];
    const selectedRange = ranges.find((range) => range.rangeId === api.range?.selectedRangeId) ?? ranges[0];
    const selectedRangeMatchesApi = selectedRange.rangeId === api.range?.selectedRangeId;
    const rangeLabel = stringValue(
        selectedRangeMatchesApi ? api.range?.rangeLabel ?? selectedRange.label : selectedRange.label,
        DEFAULT_RANGE_LABEL,
    );
    const selectedRangeId = selectedRangeMatchesApi
        ? stringValue(api.range?.selectedRangeId ?? selectedRange.rangeId, selectedRange.rangeId)
        : selectedRange.rangeId;
    const rangeStartAt = selectedRangeMatchesApi
        ? api.range?.rangeStartAt ?? selectedRange.startAt
        : selectedRange.startAt;
    const rangeEndAt = selectedRangeMatchesApi
        ? api.range?.rangeEndAt ?? selectedRange.endAt
        : selectedRange.endAt;
    const rangeStatus = selectedRangeMatchesApi
        ? api.range?.status ?? selectedRange.status
        : selectedRange.status;
    const rangeGeneratedAt = selectedRangeMatchesApi
        ? api.range?.generatedAt ?? selectedRange.generatedAt
        : selectedRange.generatedAt;
    const sourceRangeStartAt = selectedRangeMatchesApi
        ? api.range?.sourceRangeStartAt ?? selectedRange.sourceRangeStartAt
        : selectedRange.sourceRangeStartAt;
    const sourceRangeEndAt = selectedRangeMatchesApi
        ? api.range?.sourceRangeEndAt ?? selectedRange.sourceRangeEndAt
        : selectedRange.sourceRangeEndAt;
    const rangeEmptyReason = selectedRangeMatchesApi
        ? api.range?.emptyReason ?? selectedRange.emptyReason
        : selectedRange.emptyReason;
    const selectedRangeIsCurrentCycle = typeof api.range?.isCurrentCycle === 'boolean'
        ? api.range.isCurrentCycle
        : selectedRange.isCurrentCycle ?? true;
    const fallbackLabels = getLocalizedAdapterFallbacks(options.locale);
    const outcomes = (api.outcomes ?? []).map((outcome) => toOutcome(outcome, rangeLabel, generatedAt, fallbackLabels));
    const memberTaskCount = numberValue(api.summary?.memberTaskCount ?? api.summary?.userTaskCount);
    const visibleOutcomeCount = numberValue(api.summary?.visibleOutcomeCount, outcomes.length);
    const overview = toOverview(api, memberTaskCount);

    return {
        schemaVersion: stringValue(api.schemaVersion, 'outcome-ledger-v1'),
        status: stringValue(api.status, 'empty'),
        reasonCode: optionalString(api.reasonCode),
        ctaAction: optionalString(api.ctaAction),
        currentGeneration: toCurrentGeneration(api.currentGeneration),
        generatedAt,
        sourceEnvironment: stringValue(options.environment, 'prod'),
        workspace: {
            workspaceId: stringValue(api.workspace?.workspaceId, ''),
            workspaceName,
            workspaceAvatar: api.workspace?.workspaceAvatar ?? null,
            initials: stringValue(api.workspace?.initials, deriveInitials(workspaceName)),
            viewerRole: stringValue(api.workspace?.viewerRole ?? options.viewerRole, fallbackLabels.ownerView),
            ledgerTitle: stringValue(api.workspace?.ledgerTitle, '成果账本'),
            lastGeneratedAt: stringValue(api.workspace?.lastGeneratedAt ?? generatedAt, generatedAt),
            ledgerRefreshSchedule: api.ledgerRefreshSchedule
                ? {
                    frequency: stringValue(api.ledgerRefreshSchedule.frequency, 'daily'),
                    localTime: stringValue(api.ledgerRefreshSchedule.localTime, '08:00'),
                    internalTriggerLocalTime: optionalString(api.ledgerRefreshSchedule.internalTriggerLocalTime),
                    timezone: stringValue(api.ledgerRefreshSchedule.timezone, timezone),
                }
                : null,
            timezone,
        },
        range: {
            availableRanges: ranges,
            selectedRangeId,
            rangeLabel,
            rangeStartAt: stringValue(rangeStartAt, ''),
            rangeEndAt: stringValue(rangeEndAt, ''),
            status: optionalString(rangeStatus),
            generatedAt: optionalString(rangeGeneratedAt),
            sourceRangeStartAt: optionalString(sourceRangeStartAt),
            sourceRangeEndAt: optionalString(sourceRangeEndAt),
            emptyReason: optionalString(rangeEmptyReason),
            isCurrentCycle: selectedRangeIsCurrentCycle,
            subscriptionCycleNo: null,
        },
        summary: {
            memberTaskCount,
            bustlyOutcomeCount: numberValue(api.summary?.bustlyOutcomeCount, outcomes.length),
            visibleOutcomeCount,
            estimatedTimeSavedMinutes: null,
            estimatedLaborCostAmount: null,
            estimatedEconomicValueAmount: null,
            currency: 'USD',
            valueCalculationStatus: stringValue(api.summary?.valueCalculationStatus, 'trace_derived_pending_value_model'),
            valueInputsMissing: api.summary?.valueInputsMissing ?? [],
            totalDisplayCostUsd: numberValue(api.summary?.totalDisplayCostMicrousd) / 1_000_000,
            totalTokens: numberValue(api.summary?.totalTokens),
        },
        overview,
        filters: {
            outcomeTypeCounts: filterCounts(api.filters?.outcomeTypeFilters),
            businessModuleCounts: filterCounts(api.filters?.businessAreaFilters),
            taskTypeCounts: [],
            acceptanceStatusCounts: filterCounts(api.filters?.acceptanceStatusFilters),
        },
        outcomes,
        tasks: toTasks(outcomes),
    };
};
